import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, Chip, DataTable, EmptyState, Icon, Modal, Pane, PaneHead, PhoneCards, PhoneScreen, PickerChip,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import {
  COUNT_REASONS,
  dayMonth,
  useCountActions,
  useCountSheet,
  useCounts,
  type CountLine,
  type CountReason,
  type CountSheetRow,
  type StockCount,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · Count — `g_count_start` (f6a), `g_count_open` (f6) and the walk
 * `g_count` (f6b). THREE STATES OF ONE SCREEN.
 *
 * ONE COUNT AT A TIME, and it stays open until it is closed, so everybody
 * walking the shelf means the same thing by "the count". Starting one does NOT
 * lock the shelf and does not stop consumption — you count what you touch, and
 * whatever you never reach simply stays uncounted. Partial counts are the
 * normal case, not a failure.
 *
 * NOTHING MOVES UNTIL YOU CLOSE. The whole screen is a draft of corrections;
 * closing is the commit, and it is the moment the shelf changes and the
 * managers hear about every variance.
 *
 * A VARIANCE CANNOT BE SAVED WITHOUT A REASON AND A NOTE — enforced in the
 * form and again in the service, because a number that differs from the
 * running total and does not explain itself is the silent edit the movement
 * log exists to prevent.
 *
 * THE FIRST COUNT OF AN ITEM SEEDS ITS OPENING BALANCE. That is go-live: a
 * clinic arriving with a full cupboard imports nothing, it counts once.
 */

type Filter = 'all' | 'variances' | 'uncounted' | 'tracked';

const NONE: StockCount[] = [];

export function StockCountPage() {
  const [params, setParams] = useSearchParams();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const { data, isLoading, isError } = useCounts();
  const counts = data ?? NONE;
  const open = counts.find((c) => c.status === 'open') ?? null;
  const lastClosed = counts.find((c) => c.status === 'closed') ?? null;

  /* `?walk=1` is the third state: the shelf walk itself. Kept in the URL so
     the screen can be returned to, and so the two list states stay one page. */
  const walking = params.get('walk') === '1' && !!open;
  const setWalking = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set('walk', '1');
    else next.delete('walk');
    setParams(next, { replace: true });
  };

  const { data: sheet, isLoading: sheetLoading } = useCountSheet(walking && open ? open.id : null);
  const act = useCountActions();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [counting, setCounting] = useState<CountSheetRow | null>(null);
  const [closing, setClosing] = useState(false);
  const [pickedId, setPicked] = useState<string | null>(null);

  usePageMeta({ title: 'Count' });

  const lineFor = (itemId: string): CountLine | undefined =>
    open?.lines.find((l) => l.itemId === itemId);

  const walkRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (sheet?.shelf ?? []).filter((row) => {
      const line = lineFor(row.itemId);
      if (filter === 'variances' && (!line || line.variance === 0)) return false;
      if (filter === 'uncounted' && line) return false;
      if (filter === 'tracked' && !row.batchTracked) return false;
      if (q && !row.name.toLowerCase().includes(q)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, filter, query, open?.lines]);

  const picked = counts.find((c) => c.id === pickedId) ?? null;
  const [deskPickId, setDeskPick] = useState<string | null>(null);
  const rows: TableRow[] = !open ? [] : walkRows.map((row) => {
    const line = lineFor(row.itemId);
    return {
      id: row.itemId,
      cells: {
        item: (
          <div style={{ minWidth: 0 }}>
            <p className="t-strong">{row.name}</p>
            <p className="t-cap mut">
              {row.unit || 'item'}
              {row.batchTracked ? ' · batch-tracked' : ''}
              {row.seedsOpening ? ' · first count seeds its opening balance' : ''}
            </p>
          </div>
        ),
        expected: <span className="t-strong num">{line?.expected ?? row.expected}</span>,
        counted: line ? (
          <span className={`t-strong num${line.variance !== 0 ? ' red' : ''}`}>
            {line.counted} {canWrite ? <Icon name="pencil" /> : null}
          </span>
        ) : (
          <span className="mut">—</span>
        ),
        variance: !line ? (
          <span className="t-sec mut">not counted yet</span>
        ) : line.variance === 0 ? (
          <span className="t-strong grn">✓ matches</span>
        ) : (
          <span className="t-sec red">
            {line.variance > 0 ? `+${line.variance}` : line.variance} · {line.reason}
            {line.note ? ` — ${line.note}` : ''}
          </span>
        ),
      },
    };
  });


  /* `g_k5`: a card is "Name … expected N  counted ✓"; a variance turns it red
     and says what it still needs. */
  const phoneWalkRows: TableRow[] = !open ? [] : walkRows.map((row) => {
    const line = lineFor(row.itemId);
    return {
      id: row.itemId,
      cells: {
        item: (
          <div style={{ minWidth: 0 }}>
            <p className="t-strong">{row.name}</p>
            {line && line.variance !== 0 ? (
              <p className="t-cap red mt4">
                {line.variance > 0 ? `+${line.variance}` : `− ${Math.abs(line.variance)}`}
                {line.note ? ` · ${line.note}` : ' · note required'}
              </p>
            ) : null}
          </div>
        ),
        figs: (
          <span className="flex" style={{ gap: 8, alignItems: 'baseline' }}>
            <span className="t-sec mut">expected {line?.expected ?? row.expected}</span>
            {line ? (
              <span className={`t-strong${line.variance !== 0 ? ' red' : ''}`}>
                {line.counted}{line.variance === 0 ? ' ✓' : ' '}
                {line.variance !== 0 ? <Icon name="pencil" /> : null}
              </span>
            ) : (
              <span className="mut">—</span>
            )}
          </span>
        ),
      },
    };
  });


  const saveCount = (row: CountSheetRow, counted: number, reason?: CountReason, note?: string) => {
    if (!open) return;
    act.record.mutate(
      {
        countId: open.id,
        itemId: row.itemId,
        counted,
        expected: row.expected,
        ...(counted !== row.expected ? { reason, note } : {}),
      },
      {
        onSuccess: () =>
          toast.success(
            counted === row.expected
              ? `${row.name} ✓ matches`
              : `${row.name}: ${counted} counted against ${row.expected}`,
          ),
        onError: (e: unknown) =>
          toast.error(
            (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data
              ?.error?.message ?? 'It was not saved.',
          ),
      },
    );
  };
  const deskRow = (sheet?.shelf ?? []).find((r) => r.itemId === deskPickId) ?? null;

  useShellChrome(
    walking && open
      ? {
          raw: true,
          title: `Count · ${branchName}`,
          sub: `${open.counted} of ${open.ofItems} counted${open.variances ? ` · ${open.variances} variance${open.variances === 1 ? '' : 's'}` : ''}`,
          search: 'Scan or search to jump to a row',
          searchValue: query,
          onSearchChange: setQuery,
          secondary: { label: '‹ Back to counts', onClick: () => setWalking(false) },
          ...(canWrite
            ? {
                primary: {
                  label: 'Close the count',
                  disabled: open.counted === 0 || act.close.isPending,
                  onClick: () => setClosing(true),
                },
              }
            : {}),
          pane: deskRow ? (
            <VariancePane
              key={deskRow.itemId}
              row={deskRow}
              existing={lineFor(deskRow.itemId)}
              onSave={(c, r, n) => { saveCount(deskRow, c, r, n); setDeskPick(null); }}
              onClose={() => setDeskPick(null)}
            />
          ) : (
            <WalkPane count={open} branchName={branchName} />
          ),
          phone: (
            <PhoneScreen
              className="stock-phone"
              crumb="Stock"
              title={`Count · ${branchName}`}
              onBack={() => setWalking(false)}
              menu={(close) => <StockMenu door="count" onClose={close} />}
            >
              <p className="t-sec mut mb8">Count what you touch — partial counts are fine</p>
              <PhoneCards
                columns={[
                  { key: 'item', label: 'Item', width: 60 },
                  { key: 'figs', label: 'Counted', width: 40 },
                ]}
                rows={phoneWalkRows}
                body="item"
                trailing="figs"
                cardStyle={(r) => {
                  const l = lineFor(r.id);
                  return l && l.variance !== 0 ? { borderColor: 'var(--danger-600)' } : undefined;
                }}
                onOpen={(r) => {
                  const row = (sheet?.shelf ?? []).find((x) => x.itemId === r.id);
                  if (row && canWrite) setCounting(row);
                }}
                empty={sheetLoading ? <EmptyState title="Getting the shelf…" /> : <EmptyState title="Nothing to walk"
                  sub="Counting does not lock the shelf — you count what you touch, and what you never reach stays uncounted." />}
              />
              {canWrite ? (
                <button type="button" className="btn btn-primary mt12"
                  disabled={open.counted === 0 || act.close.isPending}
                  onClick={() => setClosing(true)}>
                  Close the count
                </button>
              ) : null}
              <p className="t-cap mut mt12" style={{ textAlign: 'center' }}>
                variances → the manager · snapshot logged
              </p>
            </PhoneScreen>
          ),
        }
      : {
          raw: true,
          /* Phone: a past count is its own screen; back clears it. */
          paneOpen: !!picked,
          onPaneClose: () => setPicked(null),
          paneMenu: (close) => <StockMenu door="count" onClose={close} />,
          title: `Count · ${branchName}`,
          sub: open
            ? `one count is open${lastClosed ? ` · last closed ${dayMonth(lastClosed.closedAt)}` : ''}`
            : lastClosed
              ? `no count open · last closed ${dayMonth(lastClosed.closedAt)}`
              : 'no count has been taken yet',
          search: null,
          ...(canWrite
            ? open
              ? {
                  secondary: { label: 'Start a count ▸', onClick: () => startBlocked() },
                  primary: { label: 'Continue the count', onClick: () => setWalking(true) },
                }
              : {
                  primary: {
                    label: 'Start a count',
                    disabled: act.start.isPending,
                    onClick: () =>
                      act.start.mutate(undefined, {
                        onSuccess: () => {
                          toast.success('Count opened — walk the shelf and count what you touch');
                          setWalking(true);
                        },
                        onError: () => toast.error('The count was not opened.'),
                      }),
                  },
                }
            : {}),
          pane: picked ? (
            <ClosedPane
              count={picked}
              onClose={() => setPicked(null)}
              onContinue={canWrite && picked.status === 'open' ? () => setWalking(true) : undefined}
              onCloseCount={canWrite && picked.status === 'open' ? () => { setWalking(true); } : undefined}
            />
          ) : (
            <Pane
              head={
                <PaneHead
                  overline="Count"
                  title={open ? 'One count is open' : 'No count is open'}
                  sub={
                    open
                      ? `${open.counted} of ${open.ofItems} counted so far`
                      : 'starting one does not lock the shelf'
                  }
                />
              }
            >
              <div className="emptystate">
                <p className="t-sec">
                  A count stays open until you close it, so there is never more than one. Nothing on
                  the shelf moves until then.
                </p>
              </div>
            </Pane>
          ),
        },
    [walking, open, lastClosed, picked, canWrite, query, branchName, act.start.isPending, act.close.isPending, deskRow, sheet, walkRows, filter],
  );

  const startBlocked = () =>
    toast.error('A count is already open — continue that one, or close it first');

  /* ── the walk (f6b) ─────────────────────────────────────────────────────── */
  if (walking && open) {
    return (
      <div className="maie desk stock-root">
        <div className="filterrow">
          {(
            [
              ['all', 'All rows'],
              ['variances', 'Only variances'],
              ['uncounted', 'Not counted yet'],
              ['tracked', 'Batch-tracked'],
            ] as Array<[Filter, string]>
          ).map(([key, label]) => (
            <Chip
              key={key}
              kind={filter === key ? 'brand' : 'outline'}
              small
              onClick={() => setFilter(key)}
            >
              {label}
              {filter === key ? ' ✓' : ''}
            </Chip>
          ))}
        </div>

        <div className="dcontent scroll">
          <DataTable
            caption={`The ${branchName} shelf walk`}
            columns={[
              { key: 'item', label: 'Item', width: 40 },
              { key: 'expected', label: 'Expected', width: 13 },
              { key: 'counted', label: 'Counted', width: 12 },
              { key: 'variance', label: 'Variance', width: 35 },
            ]}
            rows={rows}
            selectedId={deskPickId}
            onSelect={canWrite ? (r) => setDeskPick(r.id === deskPickId ? null : r.id) : undefined}
            empty={sheetLoading ? (
              <div className="emptystate"><p className="es-t">Getting the shelf…</p></div>
            ) : (
              <div className="emptystate">
                <p className="es-t">
                  {filter === 'variances'
                    ? 'No variances'
                    : filter === 'uncounted'
                      ? 'Everything has been counted'
                      : 'Nothing to walk'}
                </p>
                <p className="t-sec mut">
                  {filter === 'all'
                    ? 'The catalog is empty — there is nothing on the shelf to count.'
                    : 'Clear the filter to see the rest of the shelf.'}
                </p>
              </div>
            )}
          />
          <p className="t-sec mut mt16">
            Count what you touch — partial counts are fine. The FIRST count of an item seeds its
            opening balance; that is go-live. Expected versus counted, note-gated variance.
          </p>
        </div>

        <CountModal
          row={counting}
          existing={counting ? lineFor(counting.itemId) : undefined}
          onClose={() => setCounting(null)}
          onSave={(counted, reason, note) => {
            if (!counting) return;
            saveCount(counting, counted, reason, note);
            setCounting(null);
          }}
        />

        <CloseModal
          open={closing}
          count={open}
          onClose={() => setClosing(false)}
          onConfirm={() => {
            act.close.mutate(open.id, {
              onSuccess: (r) => {
                toast.success(
                  r.variances
                    ? `Count closed — ${r.variances} variance${r.variances === 1 ? '' : 's'} corrected the shelf, and the managers were told`
                    : 'Count closed — everything matched',
                );
                setWalking(false);
              },
              onError: () => toast.error('The count was not closed. Nothing has been changed.'),
            });
            setClosing(false);
          }}
        />
      </div>
    );
  }

  /* ── the list (f6) and the no-count state (f6a) ─────────────────────────── */
  const listRows: TableRow[] = counts.map((c) => ({
    id: c.id,
    cells: {
      count: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">
            {c.status === 'open' ? 'Open count' : 'Closed count'} · {branchName}
          </p>
          <p className="t-cap mut">
            {c.status === 'open'
              ? `started by ${c.startedBy ?? 'somebody'} — count what you touch`
              : `closed by ${c.closedBy ?? 'somebody'}`}
          </p>
        </div>
      ),
      started: <span className="mut">{dayMonth(c.startedAt)}</span>,
      counted: (
        <span className="t-strong">
          {c.counted} of {c.ofItems}
        </span>
      ),
      state:
        c.status === 'open' ? (
          <Chip kind="warn" small>
            open{c.variances ? ` · ${c.variances} variance${c.variances === 1 ? '' : 's'}` : ''}
          </Chip>
        ) : (
          <Chip kind="success" small>closed ✓</Chip>
        ),
    },
  }));

  return (
    <div className="maie desk stock-root">
      <div className="dcontent scroll">
        {counts.length === 0 && !isLoading ? (
          <>
            <div className="emptystate">
              <p className="es-t">No count is open at {branchName}</p>
              <p className="t-sec mut">
                A count stays open until you close it, so there is never more than one. Starting a
                count does not lock the shelf and does not stop consumption: you walk the shelf and
                count what you touch, and whatever you never reach simply stays uncounted.
              </p>
            </div>

            <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>What starting one does</p>
            <div className="stock-count-doors" style={{ display: 'flex', gap: 12 }}>
              <Card style={{ flex: 1, margin: 0 }}>
                <p className="t-ovl mut">Seeds the opening balance</p>
                <p className="t-strong mt8">the FIRST count of an item, ever</p>
                <p className="t-cap mut mt8">
                  that is go-live: opening balances need no import, you count once
                </p>
              </Card>
              <Card style={{ flex: 1, margin: 0 }}>
                <p className="t-ovl mut">Expected versus counted</p>
                <p className="t-strong mt8">the running number is the expectation</p>
                <p className="t-cap mut mt8">
                  a difference is a variance, and a variance cannot be saved without a reason and a
                  note
                </p>
              </Card>
              <Card style={{ flex: 1, margin: 0 }}>
                <p className="t-ovl mut">Closing is the commit</p>
                <p className="t-strong mt8">nothing moves until you close</p>
                <p className="t-cap mut mt8">
                  closing corrects the running numbers and alerts the clinic manager with every
                  variance note attached
                </p>
              </Card>
            </div>
          </>
        ) : (
          <DataTable
            caption="Counts at this branch"
            columns={[
              { key: 'count', label: 'Count', width: 46 },
              { key: 'started', label: 'Started', width: 18 },
              { key: 'counted', label: 'Counted', width: 14 },
              { key: 'state', label: 'State', width: 22 },
            ]}
            rows={listRows}
            selectedId={pickedId}
            onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
            empty={
              <div className="emptystate">
                <p className="t-sec mut">
                  {isLoading
                    ? 'Reading the counts…'
                    : isError
                      ? 'The counts did not come back. Nothing has been changed.'
                      : ''}
                </p>
              </div>
            }
          />
        )}

        <p className="t-sec mut mt16">
          Partial counts are fine — a count stays open until you close it, and the FIRST count of an
          item seeds its opening balance. That is go-live: opening balances need no import, you
          count once.
        </p>
        <p className="t-sec mut mt12">
          The shelf walk itself is a phone job — this desk screen exists for a seated recount or a
          go-live session.
        </p>
      </div>
    </div>
  );
}

/** The docked count while walking: how far, and what has not matched. */
function WalkPane({ count, branchName }: { count: StockCount; branchName: string }) {
  const varied = count.lines.filter((l) => l.variance !== 0);
  return (
    <Pane
      head={
        <PaneHead
          overline={`Open count · ${branchName}`}
          title={`${count.counted} of ${count.ofItems}`}
          sub={`started ${dayMonth(count.startedAt)} by ${count.startedBy ?? 'somebody'}`}
        />
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">Counted</p>
          <p className="t-heading mt8">{count.counted}</p>
        </div>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">Variances</p>
          <p className={`t-heading mt8${varied.length ? ' amb' : ''}`}>{varied.length}</p>
        </div>
      </div>

      {varied.length ? (
        <>
          <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>What did not match</p>
          {varied.map((l) => (
            <Card key={l.id} style={{ borderColor: 'var(--warning-600)' }}>
              <div className="flex">
                <p className="t-strong grow">{l.itemName}</p>
                <p className="t-strong red">
                  {l.variance > 0 ? `+${l.variance}` : l.variance}
                </p>
              </div>
              <p className="t-cap mut mt8">
                expected {l.expected}, counted {l.counted} · {l.reason}
              </p>
              {l.note ? <p className="t-sec sec mt4">{l.note}</p> : null}
            </Card>
          ))}
        </>
      ) : (
        <Card>
          <p className="t-sec mut">
            {count.counted
              ? 'Everything counted so far matches the running numbers.'
              : 'Nothing counted yet. Count what you touch — you do not have to reach the whole shelf.'}
          </p>
        </Card>
      )}

      <Card kind="accent">
        <p className="t-sec brand">
          Nothing on the shelf has moved. Closing the count is the commit — it corrects the running
          numbers and tells the clinic’s managers, with every note attached.
        </p>
      </Card>
    </Pane>
  );
}

/**
 * `g_count` — THE VARIANCE EDITOR. One item docked: what the running number
 * says, what was counted, and — only when they differ — the reason and the note
 * that the count cannot be saved without. Saving a variance does not move the
 * shelf; closing the count does.
 */
function VariancePane({
  row,
  existing,
  onSave,
  onClose,
}: {
  row: CountSheetRow;
  existing?: CountLine;
  onSave: (counted: number, reason: CountReason | undefined, note: string | undefined) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(existing ? String(existing.counted) : '');
  const [reason, setReason] = useState<CountReason>(existing?.reason ?? 'miscount');
  const [note, setNote] = useState(existing?.note ?? '');
  const counted = value.trim() === '' ? null : Number(value);
  const variance = counted === null ? null : counted - row.expected;
  const needsWhy = variance !== null && variance !== 0;
  const ready = counted !== null && counted >= 0 && (!needsWhy || note.trim().length > 2);
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Variance"
          title={row.name}
          sub={`${row.expected} expected${counted !== null ? ` · ${counted} counted` : ''}${needsWhy ? ` · ${variance! > 0 ? '+' : '−'} ${Math.abs(variance!)}` : ''}`}
        />
      }
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={() => { setValue(''); setNote(''); }}>Recount</button>
          <button type="button" className="btn btn-primary" disabled={!ready}
            onClick={() => counted !== null && onSave(counted, needsWhy ? reason : undefined, needsWhy ? note.trim() : undefined)}>
            {needsWhy ? 'Save the variance' : '✓ Matches'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">Expected</p>
          <p className="t-heading mt8">{row.expected}</p>
        </div>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">Counted</p>
          <input className="fld t-heading num mt8" type="number" min={0} inputMode="numeric" autoFocus
            aria-label={`How many ${row.name}`} placeholder={String(row.expected)}
            value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className="card" style={{ flex: 1, margin: 0 }}>
          <p className="t-ovl mut">Variance</p>
          <p className={`t-heading mt8${needsWhy ? ' red' : ''}`}>
            {variance === null ? '—' : variance === 0 ? '0' : `${variance > 0 ? '+' : '−'} ${Math.abs(variance)}`}
          </p>
        </div>
      </div>
      {row.seedsOpening ? (
        <Card kind="accent">
          <p className="t-sec brand">
            Nothing has ever been counted or received for this item, so this count IS its opening balance.
          </p>
        </Card>
      ) : null}
      {needsWhy ? (
        <>
          <Card>
            <p className="t-ovl mut">Reason (required)</p>
            <p className="mt4">
              <PickerChip
                label={`${COUNT_REASONS.find((r) => r.value === reason)?.label} ▾`}
                ariaLabel="Reason"
                kind="brand"
                value={reason}
                onChange={setReason}
                options={COUNT_REASONS}
              />
            </p>
            <input className="fld t-sec mt8" aria-label="Note" placeholder="what happened, in your own words"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </Card>
          <Card style={{ borderColor: 'var(--warning-600)' }}>
            <p className="t-sec amb">
              Closing corrects the running number and alerts the clinic manager with this note.
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Where it goes</p>
            <p className="t-strong mt4">Clinic manager ▸ Alerts</p>
            <p className="t-cap mut mt4">
              a number never moves without a line of yours attached
            </p>
          </Card>
        </>
      ) : (
        <p className="t-cap mut">Nothing on the shelf moves until the count is closed.</p>
      )}
    </Pane>
  );
}

/** "08:40" from an instant, in the viewer's own clock. */
function clockOf(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** A closed count, read back — what it corrected and who closed it. */
function ClosedPane({
  count,
  onClose,
  onContinue,
  onCloseCount,
}: {
  count: StockCount;
  onClose: () => void;
  onContinue?: () => void;
  onCloseCount?: () => void;
}) {
  const varied = count.lines.filter((l) => l.variance !== 0);
  return (
    <Pane
      onClose={onClose}
      foot={
        count.status === 'open' && (onContinue || onCloseCount) ? (
          <>
            {onContinue ? <button type="button" className="btn btn-primary" onClick={onContinue}>Continue</button> : null}
            {onCloseCount ? <button type="button" className="btn btn-outline" onClick={onCloseCount}>Close the count</button> : null}
          </>
        ) : undefined
      }
      head={
        <PaneHead
          overline={count.status === 'open' ? 'Count' : 'Closed count'}
          title={count.status === 'open' ? `Open since ${clockOf(count.startedAt)}` : `${count.counted} of ${count.ofItems} counted`}
          sub={
            count.status === 'open'
              ? `started ${dayMonth(count.startedAt)} by ${count.startedBy ?? 'somebody'}`
              : `closed ${dayMonth(count.closedAt)} by ${count.closedBy ?? 'somebody'}`
          }
        />
      }
    >
      {count.status === 'open' ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div className="card" style={{ flex: 1, margin: 0 }}>
              <p className="t-ovl mut">Counted</p>
              <p className="t-heading mt8">{count.counted}</p>
            </div>
            <div className="card" style={{ flex: 1, margin: 0 }}>
              <p className="t-ovl mut">Of</p>
              <p className="t-heading mt8">{count.ofItems}</p>
            </div>
            <div className="card" style={{ flex: 1, margin: 0 }}>
              <p className="t-ovl mut">Variances</p>
              <p className={`t-heading mt8${varied.length ? ' amb' : ''}`}>{varied.length}</p>
            </div>
          </div>
          <Card>
            <p className="t-ovl mut">Started</p>
            <p className="t-strong mt4">{dayMonth(count.startedAt)} {clockOf(count.startedAt)} · {count.startedBy ?? 'somebody'}</p>
            <p className="t-cap mut mt4">
              this desk screen is for a seated recount — the shelf walk itself is done on a phone
            </p>
          </Card>
        </>
      ) : null}
      {varied.length === 0 ? (
        <Card>
          <p className="t-sec mut">
            Every item counted matched the running number — the shelf needed no correction.
          </p>
        </Card>
      ) : (
        <>
          <p className="t-ovl mut" style={{ margin: '4px 0 6px' }}>
            {count.status === 'open' ? 'What has not matched' : 'What it corrected'}
          </p>
          {varied.map((l) => (
            <Card key={l.id}>
              <div className="flex">
                <p className="t-strong grow">{l.itemName}</p>
                <p className="t-strong">{l.variance > 0 ? `+${l.variance}` : l.variance}</p>
              </div>
              <p className="t-cap mut mt8">
                expected {l.expected}, counted {l.counted} · {l.reason}
                {l.countedBy ? ` · ${l.countedBy}` : ''}
              </p>
              {l.note ? <p className="t-sec sec mt4">{l.note}</p> : null}
            </Card>
          ))}
        </>
      )}
    </Pane>
  );
}

/** One item, counted. The note gate lives here and again in the service. */
function CountModal({
  row,
  existing,
  onClose,
  onSave,
}: {
  row: CountSheetRow | null;
  existing?: CountLine;
  onClose: () => void;
  onSave: (counted: number, reason: CountReason | undefined, note: string | undefined) => void;
}) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState<CountReason>('miscount');
  const [note, setNote] = useState('');

  const counted = value.trim() === '' ? (existing?.counted ?? null) : Number(value);
  const variance = counted === null || !row ? null : counted - row.expected;
  const needsWhy = variance !== null && variance !== 0;
  const ready = counted !== null && counted >= 0 && (!needsWhy || note.trim().length > 2);

  return (
    <Modal
      open={!!row}
      onClose={onClose}
      title={row ? row.name : 'Count'}
      sub={row ? `the running number says ${row.expected}` : undefined}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() => {
              if (counted === null) return;
              onSave(counted, needsWhy ? reason : undefined, needsWhy ? note.trim() : undefined);
              setValue('');
              setNote('');
            }}
          >
            {variance === 0 ? '✓ Matches' : 'Save the count'}
          </button>
        </>
      }
    >
      {row ? (
        <>
          {row.seedsOpening ? (
            <Card kind="accent">
              <p className="t-sec brand">
                Nothing has ever been counted or received for this item, so this count IS its
                opening balance.
              </p>
            </Card>
          ) : null}

          <label className="t-ovl mut" htmlFor="cnt-n">How many are there</label>
          <input
            id="cnt-n"
            className="fld t-strong mt4 num"
            type="number"
            min={0}
            inputMode="numeric"
            autoFocus
            defaultValue={existing?.counted ?? ''}
            placeholder={String(row.expected)}
            onChange={(e) => setValue(e.target.value)}
          />

          {variance !== null ? (
            <p className={`t-sec mt8${variance === 0 ? ' grn' : ' red'}`}>
              {variance === 0
                ? '✓ matches the running number'
                : `${variance > 0 ? `+${variance}` : variance} against an expected ${row.expected}`}
            </p>
          ) : null}

          {needsWhy ? (
            <>
              <p className="t-ovl mut mt12">Why</p>
              <p className="mt4">
                <PickerChip
                  label={`${COUNT_REASONS.find((r) => r.value === reason)?.label} ▾`}
                  ariaLabel="Reason"
                  kind="brand"
                  value={reason}
                  onChange={setReason}
                  options={COUNT_REASONS}
                />
              </p>
              <label className="t-ovl mut mt12" htmlFor="cnt-note">Note</label>
              <input
                id="cnt-note"
                className="fld t-strong mt4"
                placeholder="what happened, in your own words"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <p className="t-cap mut mt8">
                A variance cannot be saved without both. It goes to the clinic’s managers when the
                count closes, with your name against it.
              </p>
            </>
          ) : (
            <p className="t-cap mut mt12">
              Nothing on the shelf moves until the count is closed.
            </p>
          )}
        </>
      ) : null}
    </Modal>
  );
}

/** Closing is the commit — so it says exactly what is about to change. */
function CloseModal({
  open,
  count,
  onClose,
  onConfirm,
}: {
  open: boolean;
  count: StockCount;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const varied = count.lines.filter((l) => l.variance !== 0);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Close the count?"
      sub="this is the moment the shelf changes"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Keep counting</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            Close it
          </button>
        </>
      }
    >
      <p className="t-sec sec">
        {count.counted} of {count.ofItems} items were counted. The rest stay as they are — a partial
        count is not a failure.
      </p>
      {varied.length ? (
        <>
          <p className="t-ovl mut mt12">What will change</p>
          {varied.map((l) => (
            <p key={l.id} className="t-sec sec mt4">
              {l.itemName}: {l.expected} → {l.counted} ({l.variance > 0 ? `+${l.variance}` : l.variance}
              , {l.reason})
            </p>
          ))}
          <p className="t-cap mut mt12">
            Each becomes a movement on the item’s history with your name and its note, and the
            clinic’s managers are told.
          </p>
        </>
      ) : (
        <p className="t-sec mut mt12">
          Everything matched, so nothing on the shelf changes — the count just closes.
        </p>
      )}
    </Modal>
  );
}
